import { useState } from 'react';
import {
  Box,
  Tooltip,
  TextField,
  Typography,
  Stack,
  Switch,
  FormGroup,
  FormControlLabel,
  styled,
  Alert,
} from '@mui/material';
import { filterTree } from './trees/treeUtils';
import PendingReportsTree from './trees/PendingReportsTree';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { GetPendingReport } from '../../../api/MyIMO';
import LoadingIndicator from '../../../components/Global/loading-indicator/loading-indicator';
import HeaderStack from './HeaderStack';

const StyledIMOInputBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '.5rem',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const PendingReports = ({ producerID }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialExpand = searchParams.get('expand') === 'true';

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [expandAll, setExpandAll] = useState(initialExpand);

  const updateParams = (newParams) => {
    const updatedParams = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const getPendingReportResult = useQuery({
    queryKey: ['getPendingReport', producerID],
    queryFn: () => GetPendingReport(producerID)(),
    enabled: !!producerID,
  });

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    updateParams({ search: value });
  };

  const handleExpandAll = () => {
    setExpandAll((prev) => {
      const updated = !prev;
      updateParams({ expand: updated });
      return updated;
    });
  };

  const filteredData =
    searchTerm && getPendingReportResult.data?.value
      ? filterTree(getPendingReportResult.data?.value, searchTerm) || {
          ...getPendingReportResult.data?.value,
          children: [],
        }
      : getPendingReportResult.data?.value;

  const showInitialLoad = getPendingReportResult.isPending;
  const showData =
    getPendingReportResult.isSuccess &&
    getPendingReportResult.data.value &&
    !showInitialLoad &&
    getPendingReportResult.data.value.name !== null;

  return (
    <Stack spacing={4}>
      {getPendingReportResult.isError && (
        <Box padding={'1rem 0'}>
          <Alert sx={{ width: 'fit-content' }} severity="warning">
            Something went wrong
          </Alert>
        </Box>
      )}
      {getPendingReportResult.isSuccess &&
        (!getPendingReportResult.data?.value || !getPendingReportResult.data?.value.children) && (
          <Box padding={'1rem 0'}>
            <Alert sx={{ width: 'fit-content' }} severity="info">
              No data to display
            </Alert>
          </Box>
        )}
      {showInitialLoad && <LoadingIndicator />}
      {showData && (
        <>
          <Stack spacing={2}>
            <Typography sx={{ padding: '1rem 0', textTransform: 'capitalize' }} variant="h6">
              {(getPendingReportResult.data?.value?.name || '').replace(
                /^([^:]+):(.+)$/,
                (_, before, after) => before.toUpperCase() + ':' + after.toLowerCase()
              )}
            </Typography>

            <HeaderStack
              cards={[
                {
                  label: 'Total Issued Count',
                  field: getPendingReportResult.data.value.issuedCount,
                  currency: false,
                },
                {
                  label: 'Total Issued Premium',
                  field: getPendingReportResult.data.value.issuedPremium,
                  currency: true,
                },
                {
                  label: 'Total Pending Count',
                  field: getPendingReportResult.data.value.pendingCount,
                  currency: false,
                },
                {
                  label: 'Total Pending Premium',
                  field: getPendingReportResult.data.value.pendingPremium,
                  currency: true,
                },
              ]}
            />
          </Stack>
          <StyledIMOInputBox>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '.5rem',
                width: {
                  xs: '100%',
                  sm: '15rem',
                },
              }}
            >
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                sx={{
                  width: {
                    xs: '100%',
                    sm: '15rem',
                  },
                }}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <Tooltip title="Search by producer name or contract">
                <InfoOutlinedIcon color={'#f3f3f3'} />
              </Tooltip>
            </Box>
            <FormGroup
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                alignItems: {
                  xs: 'flex-end',
                },
              }}
            >
              <FormControlLabel
                sx={{ margin: 0 }}
                labelPlacement="start"
                control={<Switch checked={expandAll} onChange={handleExpandAll} />}
                label="Expand all"
              />
            </FormGroup>
          </StyledIMOInputBox>
          <PendingReportsTree data={filteredData} expandAll={expandAll} />
        </>
      )}
    </Stack>
  );
};

export default PendingReports;
